import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CLIENT_TYPES, RELATIONSHIP_STATUSES, BILLING_MODELS, INVOICE_CYCLES } from "@/lib/constants";
import { Plus, Lock, Loader2, X, Pencil } from "lucide-react";

const REL_COLORS = {
  Active:      { bg: "#DCFCE7", text: "#15803D", border: "#BBF7D0" },
  "On Hold":   { bg: "#FEF3C7", text: "#B45309", border: "#FCD34D" },
  Completed:   { bg: "#E5E7EB", text: "#4B5563", border: "#D1D5DB" },
  Prospective: { bg: "#DBEAFE", text: "#2563EB", border: "#BFDBFE" },
};

export default function ClientRetainerLog() {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => { setLoading(true); api.get("/clients/list").then(r => setClients(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Phase 4</div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mt-2">Client & Retainer Log</h1>
          <p className="text-sm text-gray-500 mt-1.5">
            Lightweight CRM with billing context.
            {!isAdmin && <span className="inline-flex items-center gap-1 ml-2 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200"><Lock className="w-3 h-3" /> Billing visible to admins only</span>}
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setCreating(true)} data-testid="create-client-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New client
          </button>
        )}
      </header>

      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Client</th>
                <th className="text-left px-3 py-3 font-medium">Type</th>
                <th className="text-left px-3 py-3 font-medium">Relationship</th>
                <th className="text-right px-3 py-3 font-medium">Projects</th>
                <th className="text-right px-3 py-3 font-medium">Open tasks</th>
                {isAdmin && <>
                  <th className="text-left px-3 py-3 font-medium">Billing</th>
                  <th className="text-left px-3 py-3 font-medium">Invoice cycle</th>
                  <th className="text-right px-5 py-3 font-medium">Retainer</th>
                  <th className="px-5 py-3" />
                </>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clients.map(c => {
                const rs = REL_COLORS[c.relationship_status] || REL_COLORS.Active;
                return (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{c.name}</div>
                      {c.primary_contact && <div className="text-xs text-gray-500 mt-0.5">{c.primary_contact}</div>}
                    </td>
                    <td className="px-3 py-3 text-gray-700">{c.client_type || "—"}</td>
                    <td className="px-3 py-3">
                      {c.relationship_status ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border" style={{ backgroundColor: rs.bg, color: rs.text, borderColor: rs.border }}>
                          {c.relationship_status}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-900 font-mono">{c.active_projects}</td>
                    <td className="px-3 py-3 text-right text-gray-900 font-mono">{c.open_tasks}</td>
                    {isAdmin && <>
                      <td className="px-3 py-3 text-gray-700">{c.billing_model || "—"}</td>
                      <td className="px-3 py-3 text-gray-700">{c.invoice_cycle || "—"}</td>
                      <td className="px-3 py-3 text-right font-mono text-gray-900">
                        {c.monthly_retainer_amount ? `₹${Number(c.monthly_retainer_amount).toLocaleString("en-IN")}` : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => setEditing(c)} data-testid={`client-edit-${c.id}`} className="text-navy hover:underline inline-flex items-center gap-1 text-xs">
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      </td>
                    </>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(editing || creating) && (
        <ClientForm
          client={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={load}
        />
      )}
    </div>
  );
}

function ClientForm({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client?.name || "",
    client_type: client?.client_type || CLIENT_TYPES[0],
    relationship_status: client?.relationship_status || "Active",
    billing_model: client?.billing_model || "Not Applicable",
    invoice_cycle: client?.invoice_cycle || "Not Applicable",
    monthly_retainer_amount: client?.monthly_retainer_amount || "",
    primary_contact: client?.primary_contact || "",
    notes: client?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form, monthly_retainer_amount: form.monthly_retainer_amount === "" ? null : Number(form.monthly_retainer_amount) };
      if (client) await api.patch(`/clients/${client.id}`, payload);
      else {
        const r = await api.post("/clients", { name: form.name });
        await api.patch(`/clients/${r.data.id}`, payload);
      }
      onSaved && onSaved(); onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} data-testid="client-form-modal" className="bg-white max-w-lg w-full rounded-xl shadow-xl border border-gray-200 p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight">{client ? "Edit client" : "New client"}</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>
        <Field label="Name"><input required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="client-name-input" className="input" disabled={!!client} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Client type"><select value={form.client_type} onChange={(e) => setForm(f => ({ ...f, client_type: e.target.value }))} data-testid="client-type-select" className="input">{CLIENT_TYPES.map(t => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Relationship status"><select value={form.relationship_status} onChange={(e) => setForm(f => ({ ...f, relationship_status: e.target.value }))} data-testid="client-rel-select" className="input">{RELATIONSHIP_STATUSES.map(t => <option key={t}>{t}</option>)}</select></Field>
        </div>
        <Field label="Primary contact"><input value={form.primary_contact} onChange={(e) => setForm(f => ({ ...f, primary_contact: e.target.value }))} className="input" placeholder="Name · email · phone" /></Field>

        <div className="pt-3 border-t border-gray-100">
          <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold mb-2 inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Billing (admin only)</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Billing model"><select value={form.billing_model} onChange={(e) => setForm(f => ({ ...f, billing_model: e.target.value }))} data-testid="client-billing-select" className="input">{BILLING_MODELS.map(t => <option key={t}>{t}</option>)}</select></Field>
            <Field label="Invoice cycle"><select value={form.invoice_cycle} onChange={(e) => setForm(f => ({ ...f, invoice_cycle: e.target.value }))} data-testid="client-invoice-select" className="input">{INVOICE_CYCLES.map(t => <option key={t}>{t}</option>)}</select></Field>
          </div>
          <Field label="Monthly retainer (INR)"><input type="number" value={form.monthly_retainer_amount} onChange={(e) => setForm(f => ({ ...f, monthly_retainer_amount: e.target.value }))} data-testid="client-retainer-input" className="input" placeholder="0" /></Field>
        </div>

        <Field label="Notes"><textarea rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="input" /></Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded">Cancel</button>
          <button type="submit" disabled={busy} data-testid="client-save-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save</button>
        </div>
        <style>{`.input{width:100%;background:white;border:1px solid #d1d5db;border-radius:6px;padding:8px 10px;font-size:14px;color:#111827;outline:none}.input:focus{border-color:#2B4462;box-shadow:0 0 0 3px rgba(43,68,98,.12)}.input:disabled{background:#f9fafb;color:#6b7280}`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
